/**
 * The refusal screen — **`<AccessRefused>` from `@fiveox/shell`, not ours.**
 *
 * APP_DEVELOPMENT_GUIDE §7.4: "Do not build it — `<AccessRefused>` from the
 * shell is the screen", for the same reason `<PlatformShell>` is the shell's: a
 * rule every app has to read and obey is a rule the next app breaks. The
 * prototype's *"ProDoc could not open — Try again"* `EmptyState` was that
 * rule broken (docs/legacy/prodoc-ui.md §5); this file is a twelve-line
 * adapter and the copy, the store/console door and the org switcher are the
 * estate's.
 *
 * What the shell enforces so we do not have to:
 *
 * 1. **Link out to `apps/store`.** This app never calls store, subscription,
 *    wallet or payment. Its whole relationship with money is one read and some
 *    links.
 * 2. **Surface `entitlement_reason`.** It distinguishes "never assigned" from
 *    "subscription suspended" from "app-mgmt unreachable", and it is what lets
 *    a support advisor answer the customer's actual question.
 * 3. **The org switcher.** A subscription is held by an organisation, and this
 *    screen renders outside the shell — so a member of several orgs who opened
 *    the app under the wrong one would otherwise be refused with no way to
 *    stand in the right one. Rendered only when there is genuinely somewhere
 *    else to stand.
 *
 * And the thing the copy must never get wrong: **a lapse suppresses; it never
 * revokes** (FiveOX ADR-0042 §2, spec FR-12). Every document is still there.
 */

import { AccessRefused } from '@fiveox/shell'

import { APP_NAME, APP_SLUG, CONSOLE_URL, HELP_URL, STORE_URL } from '../lib/config'

interface PaywallOrg {
  id: string
  display_name: string
}

export function Paywall({
  reason,
  orgs,
  selectedOrgId,
  onSelectOrg,
}: {
  reason: string
  orgs?: PaywallOrg[]
  selectedOrgId?: string | null
  onSelectOrg?: (orgId: string) => void
}) {
  const selected = orgs?.find((o) => o.id === selectedOrgId) ?? orgs?.[0]
  return (
    <AccessRefused
      reason={reason}
      appName={APP_NAME}
      storeUrl={`${STORE_URL}/catalog/${APP_SLUG}`}
      consoleUrl={`${CONSOLE_URL}/access`}
      helpUrl={HELP_URL}
      // The same shape `<PlatformShell org={…}>` takes in App.tsx, so the
      // switcher here and the switcher in the bar are one prop and one setter.
      org={
        selected
          ? {
              selected: { id: selected.id, display_name: selected.display_name },
              options: orgs && orgs.length > 1 ? orgs : undefined,
              onSelect: orgs && orgs.length > 1 ? onSelectOrg : undefined,
            }
          : undefined
      }
    />
  )
}
